import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import Cookies from 'js-cookie';
import { API_BASE_URL } from '@/utils/apiFetch';
import { sanitizeErrorResponse, simplifyError } from '@/utils/errorMessage';

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta> = async (
  args,
  api,
  extraOptions
) => {
  const token = Cookies.get('jwt_token') || localStorage.getItem('jwt_token');

  // 🚫 Prevent API calls if user is not authenticated
  if (!token) {
    console.warn('🚫 Blocking API request - No token found:', args);
    return { error: { status: 401, data: { message: 'Your session has expired. Please sign in again.' } } };
  }

  const result = await fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers) => {
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      // Anti-CSRF marker (see apiFetch.ts) — required for cookie-authenticated mutations.
      headers.set('X-Requested-With', 'XMLHttpRequest');

      return headers;
    }
  })(args, api, extraOptions);

  if (result.error?.status === 401) {
    console.warn('⚠️ 401 Unauthorized - Token issue or expired.');
  }

  if (result.error) {
    const error = result.error;
    if (typeof error.status === 'number') {
      return { meta: result.meta, error: { ...error, data: sanitizeErrorResponse(error.data, error.status) } };
    }
    const message = simplifyError(error);
    if (error.status === 'CUSTOM_ERROR') {
      return { meta: result.meta, error: { ...error, error: message, data: sanitizeErrorResponse(error.data, error.status) } };
    }
    // RTK's parsing-error contract requires a string body; transport errors have
    // no body. Keep these variants intact for retry and authentication logic.
    if (error.status === 'PARSING_ERROR') {
      simplifyError(error.data); // Log any raw proxy/parser response before hiding it.
      return { meta: result.meta, error: { ...error, error: message, data: message } };
    }
    return { meta: result.meta, error: { ...error, error: message } };
  }
  return result;
};

export const apiService = createApi({
  baseQuery,
  endpoints: () => ({}),
  reducerPath: 'apiService'
});

export default apiService;
